import { useState } from "react";
import { motion } from "framer-motion";
import { Loader2, CheckCircle, Download, Mail, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const SECTIONS = [
  {
    key: "section_who",
    tag: "WHO",
    title: "About You",
    fields: [
      { name: "full_name", label: "Full name and credentials", hint: "e.g., John Smith, CFP\u00ae, ChFC\u00ae", type: "input" },
      { name: "title", label: "Title you use with clients", hint: "e.g., Retirement Income Specialist", type: "input" },
      { name: "experience", label: "How long have you been doing this work?", hint: "e.g., 12 years", type: "input" },
      { name: "background", label: "What did you do before this? Is there a background story worth telling?", hint: "Share your career journey \u2014 what brought you here", type: "textarea" },
      { name: "credentials", label: "Credentials, designations, or training", hint: "List all relevant certifications", type: "textarea" },
      { name: "states_licensed", label: "States licensed in", hint: "e.g., AZ, CA, TX, FL", type: "input" }
    ]
  },
  {
    key: "section_what",
    tag: "WHAT",
    title: "What You Do",
    fields: [
      { name: "elevator_pitch", label: "In one or two sentences, what do you do for clients?", hint: "Your concise value proposition", type: "textarea" },
      { name: "client_types", label: "What types of clients do you work with?", hint: "e.g., Pre-retirees 55-70, small business owners, physicians", type: "textarea" },
      { name: "common_problems", label: "The 2-3 most common problems clients come to you with", hint: "What keeps them up at night?", type: "textarea" },
      { name: "clients_served", label: "Approximately how many clients have you worked with?", hint: "A rough number is fine", type: "input" }
    ]
  },
  {
    key: "section_why",
    tag: "WHY",
    title: "Your Motivation",
    fields: [
      { name: "why_retirement", label: "Why retirement income specifically?", hint: "What drew you to this specialty?", type: "textarea" },
      { name: "story", label: "A story \u2014 personal or from a client \u2014 that captures why this work matters", hint: "This will be a powerful part of your presentation", type: "textarea" },
      { name: "client_feeling", label: "What do you want every client to feel after working with you?", hint: "The emotional outcome you aim for", type: "textarea" }
    ]
  },
  {
    key: "section_where",
    tag: "WHERE",
    title: "Your Practice",
    fields: [
      { name: "geographic_focus", label: "Geographic focus", hint: "e.g., Phoenix Metro, Nationwide, Southeast US", type: "input" },
      { name: "meeting_format", label: "In person, virtual, or both?", hint: "How do you typically meet with clients?", type: "input" },
      { name: "carrier_access", label: "What does Ridgemont\u2019s carrier access give clients?", hint: "How does this benefit the client compared to a captive agent?", type: "textarea" }
    ]
  },
  {
    key: "section_how",
    tag: "HOW",
    title: "Your Process",
    fields: [
      { name: "process", label: "Walk me through your process from first call to proposal", hint: "Step by step \u2014 what happens and when?", type: "textarea" },
      { name: "proposal_includes", label: "What does the no-cost proposal include?", hint: "What deliverables does the prospect receive?", type: "textarea" },
      { name: "prospect_needs", label: "What do you need from the prospect on the call?", hint: "What info do you need to build a great proposal?", type: "textarea" },
      { name: "compensation", label: "How are you compensated?", hint: "Optional \u2014 if you\u2019d like this included in the presentation", type: "textarea", optional: true }
    ]
  },
  {
    key: "section_edge",
    tag: "EDGE",
    title: "What Sets You Apart",
    fields: [
      { name: "client_feedback", label: "What do clients say about working with you?", hint: "Quotes, themes, or common feedback", type: "textarea" },
      { name: "first_impression", label: "What would you want a prospect to think after the first call?", hint: "The takeaway you\u2019re designing for", type: "textarea" },
      { name: "anything_else", label: "Anything else to include?", hint: "Anything we missed that should be in your presentation", type: "textarea", optional: true }
    ]
  }
];

const AgentIntakePage = () => {
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [submissionId, setSubmissionId] = useState(null);

  const handleChange = (sectionKey, fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [sectionKey]: { ...(prev[sectionKey] || {}), [fieldName]: value }
    }));
  };

  // Progress
  const totalFields = SECTIONS.reduce((sum, s) => sum + s.fields.filter(f => !f.optional).length, 0);
  const filledFields = SECTIONS.reduce((sum, s) => {
    return sum + s.fields.filter(f => !f.optional).filter(f => {
      const val = formData[s.key]?.[f.name];
      return val && val.trim().length > 0;
    }).length;
  }, 0);
  const progress = totalFields > 0 ? Math.round((filledFields / totalFields) * 100) : 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {};
      SECTIONS.forEach(s => { payload[s.key] = formData[s.key] || {}; });

      const res = await fetch(`${API_URL}/api/intake`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (res.ok) {
        setSubmissionId(data.id);
        setPdfUrl(`${API_URL}/api/intake/${data.id}/pdf`);
        setSubmitted(true);
      }
    } catch (err) {
      console.error('Submit error:', err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEmailPdf = () => {
    const agentName = formData.section_who?.full_name || 'Agent';
    const subject = encodeURIComponent(`${agentName} - Agent Intake Questionnaire`);
    const body = encodeURIComponent(`Hi,\n\nPlease find my completed Agent Intake Questionnaire.\n\nDownload PDF: ${pdfUrl}\n\nBest regards,\n${agentName}`);
    window.open(`mailto:hello@ridgemontfinancial.com?subject=${subject}&body=${body}`, '_blank');
  };

  if (submitted) {
    return (
      <div className="min-h-screen" style={{ background: '#F4F6F9', fontFamily: "'DM Sans', sans-serif" }}>
        <header className="w-full py-4 px-6" style={{ background: '#1B2A4A', borderBottom: '3px solid #C9A84C' }}>
          <h1 className="text-xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Ridgemont Financial</h1>
          <p className="text-xs" style={{ color: '#C9A84C' }}>Agent Intake</p>
        </header>
        <div className="max-w-2xl mx-auto px-4 py-16">
          <motion.div className="bg-white p-12 text-center shadow-lg" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}>
            <CheckCircle className="w-16 h-16 mx-auto mb-4" style={{ color: '#2A8A8A' }} />
            <h2 className="text-2xl font-bold mb-2" style={{ color: '#1B2A4A', fontFamily: "'Playfair Display', serif" }}>
              Questionnaire Complete
            </h2>
            <p className="text-gray-600 mb-8">
              Your responses have been submitted. Download your PDF or email it to our team.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
                <Button className="text-white font-semibold px-6 py-6 w-full" style={{ background: '#1B2A4A' }} data-testid="download-pdf-btn">
                  <Download className="w-5 h-5 mr-2" />Download PDF
                </Button>
              </a>
              <Button onClick={handleEmailPdf} className="font-semibold px-6 py-6" style={{ background: '#2A8A8A', color: 'white' }} data-testid="email-pdf-btn">
                <Mail className="w-5 h-5 mr-2" />Email to Ridgemont
              </Button>
            </div>
            <p className="text-xs text-gray-400 mt-6">The PDF will be AI-polished into your final presentation.</p>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#F4F6F9', fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <header className="w-full py-4 px-6" style={{ background: '#1B2A4A', borderBottom: '3px solid #C9A84C' }}>
        <h1 className="text-xl font-bold text-white" style={{ fontFamily: "'Playfair Display', serif" }}>Ridgemont Financial</h1>
        <p className="text-xs" style={{ color: '#C9A84C' }}>Agent Intake Questionnaire</p>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-10">
        {/* Intro */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-2" style={{ color: '#1B2A4A', fontFamily: "'Playfair Display', serif" }}>
            Agent Intake Questionnaire
          </h2>
          <p className="text-gray-600">
            Complete this form so we can build your personalized first-appointment presentation.
            Take your time — the more detail, the better.
          </p>
        </div>

        {/* Progress Bar */}
        <div className="bg-white p-4 mb-8 shadow-sm border">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium" style={{ color: '#1B2A4A' }}>Progress</span>
            <span className="text-sm font-semibold" style={{ color: '#2A8A8A' }}>{progress}%</span>
          </div>
          <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
            <div className="h-full transition-all duration-500 rounded-full" style={{ width: `${progress}%`, background: '#2A8A8A' }} />
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-8">
          {SECTIONS.map((section, sIdx) => (
            <motion.div
              key={section.key}
              className="bg-white shadow-sm border overflow-hidden"
              style={{ borderLeft: '4px solid #2A8A8A' }}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: sIdx * 0.1 }}
            >
              <div className="p-6 pb-0">
                <span className="inline-block px-3 py-1 text-xs font-bold tracking-wider mb-3" style={{ background: '#1B2A4A', color: '#C9A84C' }}>
                  {section.tag}
                </span>
                <h3 className="text-xl font-bold" style={{ color: '#1B2A4A', fontFamily: "'Playfair Display', serif" }}>
                  {section.title}
                </h3>
              </div>
              <Separator className="my-4" />
              <div className="p-6 pt-2 space-y-5">
                {section.fields.map((field) => (
                  <div key={field.name}>
                    <Label className="text-sm font-medium" style={{ color: '#1B2A4A' }}>
                      {field.label} {field.optional && <span className="text-gray-400 font-normal">(optional)</span>}
                    </Label>
                    <p className="text-xs text-gray-400 mt-0.5 mb-2">{field.hint}</p>
                    {field.type === 'textarea' ? (
                      <textarea
                        className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2A8A8A] min-h-[100px] resize-y"
                        value={formData[section.key]?.[field.name] || ''}
                        onChange={(e) => handleChange(section.key, field.name, e.target.value)}
                        data-testid={`field-${section.key}-${field.name}`}
                      />
                    ) : (
                      <Input
                        value={formData[section.key]?.[field.name] || ''}
                        onChange={(e) => handleChange(section.key, field.name, e.target.value)}
                        data-testid={`field-${section.key}-${field.name}`}
                      />
                    )}
                  </div>
                ))}
              </div>
            </motion.div>
          ))}

          <Button
            type="submit"
            disabled={submitting}
            className="w-full py-6 text-lg font-semibold text-white transition-colors"
            style={{ background: '#1B2A4A' }}
            data-testid="intake-submit-btn"
            onMouseEnter={(e) => e.target.style.background = '#2A8A8A'}
            onMouseLeave={(e) => e.target.style.background = '#1B2A4A'}
          >
            {submitting ? (
              <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Submitting & Generating PDF...</>
            ) : (
              <><Send className="w-5 h-5 mr-2" />Submit Questionnaire</>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default AgentIntakePage;
